import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STAFF_FOLLOWUP_TYPES, FOLLOWUP_STAGES } from '@tabita/contracts';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { followupsApi } from '@/services/followups.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

/**
 * Pastoral contact history — a log, not an editable record. Logging a new
 * entry is the only write this tab offers; a mistaken entry is corrected by
 * logging a better one, not by changing history.
 */
export function FollowUpsTab({ personId }: { readonly personId: string }) {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isLogging, setIsLogging] = useState(false);

  const query = useQuery({
    queryKey: ['followups', personId],
    queryFn: ({ signal }) => followupsApi.listForPerson(personId, signal),
  });

  const entries = query.data ?? [];

  return (
    <div className="followups-tab">
      {can('people.followup.create') &&
        (isLogging ? (
          <LogFollowupForm
            personId={personId}
            onClose={() => setIsLogging(false)}
            onLogged={() => {
              setIsLogging(false);
              void queryClient.invalidateQueries({ queryKey: ['followups', personId] });
            }}
          />
        ) : (
          <Button variant="primary" onClick={() => setIsLogging(true)}>
            {t('people.followup.log')}
          </Button>
        ))}

      {query.isPending && <Spinner label={t('common.states.loading')} />}
      {query.isSuccess && entries.length === 0 && <EmptyState title={t('people.followup.empty')} />}

      {entries.length > 0 && (
        <ul className="followup-list">
          {entries.map((entry) => (
            <li key={entry.id} className="followup-item">
              <div className="followup-item-header">
                <span className="followup-item-type">
                  {t(`people.followup.type.${entry.type}`)}
                </span>
                <span className="followup-item-stage">
                  {t(`people.followup.stage.${entry.stage}`)}
                </span>
                <span className="followup-item-date" dir="ltr">
                  {formatDate(entry.occurredAt, locale)}
                </span>
              </div>
              {entry.note && <p className="followup-item-note">{entry.note}</p>}
              {entry.nextContactAt && (
                <p className="field-hint">
                  {t('people.followup.nextContactAt')}: {formatDate(entry.nextContactAt, locale)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LogFollowupForm({
  personId,
  onClose,
  onLogged,
}: {
  personId: string;
  onClose: () => void;
  onLogged: () => void;
}) {
  const { t } = useI18n();
  const [type, setType] = useState<(typeof STAFF_FOLLOWUP_TYPES)[number]>('VISIT');
  const [stage, setStage] = useState<(typeof FOLLOWUP_STAGES)[number]>('CONTACTED');
  const [note, setNote] = useState('');
  const [nextContactAt, setNextContactAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const log = useMutation({
    mutationFn: () =>
      followupsApi.create(personId, {
        type,
        stage,
        note: note.trim() || null,
        nextContactAt: nextContactAt ? new Date(nextContactAt).toISOString() : null,
      }),
    onSuccess: onLogged,
    onError: (err: unknown) =>
      setError(
        err instanceof ApiError
          ? t(`errors.${err.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      ),
  });

  return (
    <form
      className="filters"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        log.mutate();
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="field">
        <label className="field-label" htmlFor="followup-type">
          {t('people.followup.typeLabel')}
        </label>
        <select
          id="followup-type"
          className="field-input"
          value={type}
          onChange={(event) => setType(event.target.value as (typeof STAFF_FOLLOWUP_TYPES)[number])}
        >
          {STAFF_FOLLOWUP_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`people.followup.type.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="followup-stage">
          {t('people.followup.stageLabel')}
        </label>
        <select
          id="followup-stage"
          className="field-input"
          value={stage}
          onChange={(event) => setStage(event.target.value as (typeof FOLLOWUP_STAGES)[number])}
        >
          {FOLLOWUP_STAGES.map((value) => (
            <option key={value} value={value}>
              {t(`people.followup.stage.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <Field
        label={t('people.followup.note')}
        name="note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />

      <Field
        label={t('people.followup.nextContactAt')}
        name="nextContactAt"
        type="date"
        value={nextContactAt}
        onChange={(event) => setNextContactAt(event.target.value)}
        dir="ltr"
      />

      <Button type="button" variant="ghost" onClick={onClose}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="primary" loading={log.isPending}>
        {t('common.actions.save')}
      </Button>
    </form>
  );
}
